import { useEffect, useRef, useState, useCallback } from "react";
import { Menu, X, Bug, AlertTriangle, TrendingUp, Cloud, Leaf, MessageSquare, MapPin, ArrowLeft, User, Route } from "lucide-react";
import SearchBar from "./map/SearchBar";
import MapControls from "./map/MapControls";
import DirectionsPanel from "./map/DirectionsPanel";
import BottomSheet from "./map/BottomSheet";
import { loadMapLibrary } from "../utils/mapOptimization";
import { mapStyles } from "../styles/mapTheme";

interface CommunityReport {
  id: string;
  farmerId: string;
  farmerName: string;
  village: string;
  crop: string;
  reportType: "pest" | "disease" | "yield" | "weather" | "technique";
  severity: "low" | "medium" | "high" | "critical";
  description: string;
  verified: boolean;
  confidence: number;
  corroborations: number;
  createdAt: string;
  gpsCoords?: { lat: number; lng: number };
}

interface OpenStreetMapProps {
  reports: CommunityReport[];
}

// Lesotho districts with coordinates
const lesothoDistricts = [
  { name: "Maseru", lat: -29.3151, lng: 27.4869 },
  { name: "Berea", lat: -29.0833, lng: 27.7167 },
  { name: "Leribe", lat: -28.8719, lng: 28.0378 },
  { name: "Mafeteng", lat: -29.8236, lng: 27.2386 },
  { name: "Mohale's Hoek", lat: -30.1522, lng: 27.4725 },
  { name: "Quthing", lat: -30.4000, lng: 27.7167 },
  { name: "Qacha's Nek", lat: -30.1167, lng: 28.6833 },
  { name: "Mokhotlong", lat: -29.2833, lng: 29.0667 },
  { name: "Thaba-Tseka", lat: -29.5167, lng: 28.6167 },
  { name: "Butha-Buthe", lat: -28.7667, lng: 28.2500 }
];

const categories = [
  { id: "all", label: "All Reports", icon: MapPin },
  { id: "pest", label: "Pest Issues", icon: Bug },
  { id: "disease", label: "Diseases", icon: AlertTriangle },
  { id: "weather", label: "Weather", icon: Cloud },
  { id: "yield", label: "Yield Reports", icon: TrendingUp },
  { id: "technique", label: "Techniques", icon: Leaf }
];



export default function OpenStreetMap({ reports }: OpenStreetMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const [mapReady, setMapReady] = useState(false);
  
  // UI State
  const [selectedReport, setSelectedReport] = useState<CommunityReport | null>(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [userLocation, setUserLocation] = useState<{lat: number, lng: number} | null>(null);
  const [mapType, setMapType] = useState<"map" | "satellite" | "terrain">("map");
  const [showTraffic, setShowTraffic] = useState(false);
  const [measurementMode, setMeasurementMode] = useState(false);
  const [measurementPoints, setMeasurementPoints] = useState<{lat: number, lng: number}[]>([]);
  const [totalDistance, setTotalDistance] = useState(0);
  const [compassHeading, setCompassHeading] = useState(0);
  const [locationMarker, setLocationMarker] = useState<any>(null);
  const [isLocating, setIsLocating] = useState(false);
  const currentTileLayerRef = useRef<any>(null);

  // Utility functions
  const getReportCoordinates = (report: CommunityReport) => {
    if (report.gpsCoords) {
      return report.gpsCoords;
    }
    const district = lesothoDistricts.find(d => 
      report.village.toLowerCase().includes(d.name.toLowerCase())
    ) || lesothoDistricts[0];
    const offset = 0.01;
    return {
      lat: district.lat + (Math.random() - 0.5) * offset,
      lng: district.lng + (Math.random() - 0.5) * offset
    };
  };

  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      console.error('Geolocation not supported');
      return;
    }

    setIsLocating(true);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        
        if (mapInstanceRef.current) {
          // Remove existing location marker
          if (locationMarker) {
            mapInstanceRef.current.removeLayer(locationMarker);
          }
          
          // Create location marker with pulsing animation
          const marker = window.L.circleMarker([latitude, longitude], {
            radius: 12,
            fillColor: '#4285f4',
            color: '#ffffff',
            weight: 3,
            opacity: 1,
            fillOpacity: 0.8,
            className: 'location-marker'
          });
          
          // Add accuracy circle
          const accuracyCircle = window.L.circle([latitude, longitude], {
            radius: position.coords.accuracy || 100,
            fillColor: '#4285f4',
            color: '#4285f4',
            weight: 1,
            opacity: 0.3,
            fillOpacity: 0.1
          });
          
          marker.bindPopup('📍 Your Location');
          marker.addTo(mapInstanceRef.current);
          accuracyCircle.addTo(mapInstanceRef.current);
          
          setLocationMarker(marker);
          
          // Center map on location with smooth animation
          mapInstanceRef.current.setView([latitude, longitude], 16, {
            animate: true,
            duration: 1
          });
        }
        
        setIsLocating(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        setIsLocating(false);
        
        // Show error message
        if (mapInstanceRef.current) {
          const popup = window.L.popup()
            .setLatLng([-29.3151, 27.4869])
            .setContent('❌ Location access denied or unavailable')
            .openOn(mapInstanceRef.current);
          
          setTimeout(() => {
            mapInstanceRef.current.closePopup(popup);
          }, 3000);
        }
        
        // Fallback to Maseru
        const maseru = lesothoDistricts[0];
        setUserLocation({ lat: maseru.lat, lng: maseru.lng });
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setView([maseru.lat, maseru.lng], 10);
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  }, [locationMarker]);

  const changeMapType = useCallback((type: "map" | "satellite" | "terrain") => {
    console.log('changeMapType called with:', type);
    console.log('Current mapType state:', mapType);
    
    // Force state update
    setMapType(prevType => {
      console.log('Setting mapType from', prevType, 'to', type);
      return type;
    });
    
    if (mapInstanceRef.current && window.L) {
      // Remove current tile layer
      if (currentTileLayerRef.current) {
        console.log('Removing current tile layer');
        mapInstanceRef.current.removeLayer(currentTileLayerRef.current);
      }
      
      let tileUrl = '';
      let attribution = '';
      let options: any = {};
      
      switch (type) {
        case 'satellite':
          tileUrl = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
          attribution = '© Esri';
          options = { maxZoom: 19 };
          break;
        case 'terrain':
          tileUrl = 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png';
          attribution = '© OpenTopoMap contributors';
          options = { maxZoom: 17 };
          break;
        default:
          tileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
          attribution = '© OpenStreetMap contributors';
          options = { maxZoom: 19 };
      }
      
      console.log('Adding new tile layer:', tileUrl);
      const newLayer = window.L.tileLayer(tileUrl, { 
        attribution, 
        ...options 
      });
      
      newLayer.addTo(mapInstanceRef.current);
      currentTileLayerRef.current = newLayer;
      console.log('Map type successfully changed to:', type);
    } else {
      console.error('Map instance or Leaflet not available');
    }
  }, [mapType]);

  const toggleTraffic = useCallback(() => {
    setShowTraffic(prev => !prev);
    // Traffic layer would be implemented here with a traffic tile service
    console.log('Traffic toggled:', !showTraffic);
  }, [showTraffic]);

  const toggleMeasurement = useCallback(() => {
    setMeasurementMode(prev => {
      const newMode = !prev;
      if (!newMode) {
        // Clear measurement when disabled
        setMeasurementPoints([]);
        setTotalDistance(0);
        // Remove measurement markers and lines from map
        if (mapInstanceRef.current) {
          mapInstanceRef.current.eachLayer((layer: any) => {
            if (layer.options && layer.options.isMeasurement) {
              mapInstanceRef.current.removeLayer(layer);
            }
          });
        }
      }
      return newMode;
    });
  }, []);

  const calculateDistance = useCallback((point1: {lat: number, lng: number}, point2: {lat: number, lng: number}) => {
    const R = 6371; // Earth's radius in km
    const dLat = (point2.lat - point1.lat) * Math.PI / 180;
    const dLng = (point2.lng - point1.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(point1.lat * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }, []);

  const resetCompass = useCallback(() => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setBearing(0);
      setCompassHeading(0);
    }
  }, []);

  // Simple marker update function
  const updateMarkers = useCallback((filteredReports: CommunityReport[]) => {
    if (!mapInstanceRef.current || !mapReady) return;
    
    try {
      // Clear existing markers
      markersRef.current.forEach(marker => {
        if (marker && mapInstanceRef.current) {
          mapInstanceRef.current.removeLayer(marker);
        }
      });
      markersRef.current = [];
      
      // Add new markers
      filteredReports.forEach(report => {
        const coords = getReportCoordinates(report);
        if (!coords || typeof coords.lat !== 'number' || typeof coords.lng !== 'number' || 
            isNaN(coords.lat) || isNaN(coords.lng)) {
          return;
        }
        
        try {
          const marker = window.L.circleMarker([coords.lat, coords.lng], {
            radius: 8,
            fillColor: getSeverityColor(report.severity),
            color: '#ffffff',
            weight: 2,
            opacity: 1,
            fillOpacity: 0.8
          });
          
          marker.on('click', () => setSelectedReport(report));
          marker.addTo(mapInstanceRef.current);
          markersRef.current.push(marker);
        } catch (error) {
          console.warn('Failed to create marker for report:', report.id, error);
        }
      });
    } catch (error) {
      console.error('Error updating markers:', error);
    }
  }, [mapReady]);

  // Event handlers
  const handleLocationSelect = useCallback((location: any) => {
    if (location.coordinates && mapInstanceRef.current) {
      mapInstanceRef.current.setView(location.coordinates, 15);
    }
  }, []);

  const handleVoiceSearch = useCallback(() => {
    // Voice search implementation would go here
    console.log('Voice search activated');
  }, []);



  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical": return "#ef4444";
      case "high": return "#f97316";
      case "medium": return "#eab308";
      case "low": return "#22c55e";
      default: return "#6b7280";
    }
  };

  const getReportIcon = (type: string) => {
    switch (type) {
      case "pest": return <Bug className="w-3 h-3" />;
      case "disease": return <AlertTriangle className="w-3 h-3" />;
      case "yield": return <TrendingUp className="w-3 h-3" />;
      case "weather": return <Cloud className="w-3 h-3" />;
      case "technique": return <Leaf className="w-3 h-3" />;
      default: return <MessageSquare className="w-3 h-3" />;
    }
  };

  // Initialize map
  useEffect(() => {
    let mounted = true;
    
    const initializeMap = async () => {
      try {
        if (!mapRef.current || mapInstanceRef.current) return;
        
        const L = await loadMapLibrary();
        if (!mounted) return;

        const map = L.map(mapRef.current, {
          zoomControl: false,
          attributionControl: false,
          zoomAnimation: false
        }).setView([-29.3151, 27.4869], 8);
        
        // Force map container z-index
        if (mapRef.current) {
          mapRef.current.style.zIndex = '1';
        }

        if (!mounted) {
          map.remove();
          return;
        }

        mapInstanceRef.current = map;
        const initialLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        });
        initialLayer.addTo(map);
        currentTileLayerRef.current = initialLayer;

        // Add click handler for measurement mode
        map.on('click', (e: any) => {
          if (measurementMode) {
            const newPoint = { lat: e.latlng.lat, lng: e.latlng.lng };
            setMeasurementPoints(prev => {
              const newPoints = [...prev, newPoint];
              
              // Add marker
              const marker = L.circleMarker([newPoint.lat, newPoint.lng], {
                radius: 6,
                fillColor: '#ff0000',
                color: '#ffffff',
                weight: 2,
                opacity: 1,
                fillOpacity: 1,
                isMeasurement: true
              } as any).addTo(map);
              
              // Add line if there's a previous point
              if (prev.length > 0) {
                const prevPoint = prev[prev.length - 1];
                const line = L.polyline([
                  [prevPoint.lat, prevPoint.lng],
                  [newPoint.lat, newPoint.lng]
                ], {
                  color: '#ff0000',
                  weight: 3,
                  opacity: 0.8,
                  isMeasurement: true
                } as any).addTo(map);
                
                // Calculate and update total distance
                const segmentDistance = calculateDistance(prevPoint, newPoint);
                setTotalDistance(prevTotal => prevTotal + segmentDistance);
              }
              
              return newPoints;
            });
          }
        });

        setMapReady(true);
        getCurrentLocation();
      } catch (error) {
        console.error('Failed to initialize map:', error);
      }
    };

    initializeMap();
    
    return () => {
      mounted = false;
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.remove();
        } catch (e) {
          console.warn('Error removing map:', e);
        }
        mapInstanceRef.current = null;
      }
      setMapReady(false);
    };
  }, []);

  // Update markers when reports or category changes
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current) return;
    
    const timer = setTimeout(() => {
      const filteredReports = selectedCategory === 'all' 
        ? reports 
        : reports.filter(report => report.reportType === selectedCategory);
      
      updateMarkers(filteredReports);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [reports, selectedCategory, mapReady, updateMarkers]);

  // Add user location marker
  useEffect(() => {
    if (!mapInstanceRef.current || !userLocation || !mapReady) return;
    
    try {
      const userMarker = window.L.circleMarker([userLocation.lat, userLocation.lng], {
        radius: 10,
        fillColor: '#4285f4',
        color: '#ffffff',
        weight: 3,
        opacity: 1,
        fillOpacity: 1
      }).addTo(mapInstanceRef.current);
      
      userMarker.bindPopup('Your Location');
      
      return () => {
        if (mapInstanceRef.current && userMarker) {
          try {
            mapInstanceRef.current.removeLayer(userMarker);
          } catch (e) {
            console.warn('Error removing user marker:', e);
          }
        }
      };
    } catch (error) {
      console.warn('Error creating user location marker:', error);
    }
  }, [userLocation, mapReady]);

  const filteredReports = reports.filter(report => 
    selectedCategory === 'all' || report.reportType === selectedCategory
  );

  return (
    <>
      <style>{`
        .location-marker {
          animation: locationPulse 2s infinite;
        }
        
        @keyframes locationPulse {
          0% {
            box-shadow: 0 0 0 0 rgba(66, 133, 244, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(66, 133, 244, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(66, 133, 244, 0);
          }
        }
      `}</style>
      <div className="flex h-full">
      {/* Left Sidebar with Search and Filters */}
      <div 
        className="w-80 bg-white border-r border-gray-200 flex flex-col overflow-hidden"
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200">
          <SearchBar
            onLocationSelect={handleLocationSelect}
            onVoiceSearch={handleVoiceSearch}
            placeholder="Search locations, reports..."
          />
        </div>
        
        {/* Category Filters */}
        <div className="p-4 border-b border-gray-200">
          <div className="grid grid-cols-2 gap-2">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="truncate">{category.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Reports List */}
        <div 
          className="flex-1 overflow-y-scroll p-4"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <h3 className="font-semibold text-gray-800 mb-3">Reports ({filteredReports.length})</h3>
          <div className="space-y-3">
            {filteredReports.map((report) => (
              <div
                key={report.id}
                onClick={() => {
                  setSelectedReport(report);
                  if (mapInstanceRef.current) {
                    const coords = getReportCoordinates(report);
                    mapInstanceRef.current.setView([coords.lat, coords.lng], 15);
                  }
                }}
                className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                  selectedReport?.id === report.id
                    ? 'bg-blue-50 border-blue-200 shadow-md'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <div className={`p-2 rounded-full ${
                      report.severity === 'critical' ? 'bg-red-100' :
                      report.severity === 'high' ? 'bg-orange-100' :
                      report.severity === 'medium' ? 'bg-yellow-100' :
                      'bg-green-100'
                    }`}>
                      {getReportIcon(report.reportType)}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-800 truncate">{report.farmerName}</h4>
                    <p className="text-sm text-gray-600 truncate">{report.village} • {report.crop}</p>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{report.description}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs font-medium text-gray-700 capitalize">{report.reportType}</span>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        report.severity === 'critical' ? 'bg-red-100 text-red-700' :
                        report.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                        report.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {report.severity}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Map Container */}
      <div className="flex-1 relative overflow-hidden">
        <div ref={mapRef} className="w-full h-full" />
        
        {!mapReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-gray-600">Loading map...</p>
            </div>
          </div>
        )}
        
        {/* Measurement Display */}
        {measurementMode && totalDistance > 0 && (
          <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-3 z-40">
            <div className="text-sm font-medium text-gray-800">
              Distance: {totalDistance.toFixed(2)} km
            </div>
            <button
              onClick={() => {
                setMeasurementPoints([]);
                setTotalDistance(0);
                if (mapInstanceRef.current) {
                  mapInstanceRef.current.eachLayer((layer: any) => {
                    if (layer.options && layer.options.isMeasurement) {
                      mapInstanceRef.current.removeLayer(layer);
                    }
                  });
                }
              }}
              className="text-xs text-red-600 hover:text-red-800 mt-1"
            >
              Clear
            </button>
          </div>
        )}

        {/* Map Controls */}
        {mapReady && (
          <div className="absolute bottom-6 right-6 z-40">
            <MapControls
              onZoomIn={() => mapInstanceRef.current?.zoomIn()}
              onZoomOut={() => mapInstanceRef.current?.zoomOut()}
              onMyLocation={getCurrentLocation}
              onMapTypeChange={changeMapType}
              onTrafficToggle={toggleTraffic}
              onMeasurementToggle={toggleMeasurement}
              onCompassReset={resetCompass}
              mapType={mapType}
              showTraffic={showTraffic}
              measurementMode={measurementMode}
              compassHeading={compassHeading}
              isLocating={isLocating}
            />
          </div>
        )}
      </div>
      </div>
    </>
  );
}